'use client';
import { type AuthViews } from './constants';
import {
  LinkProps,
  ToutContentProps,
  YoutubeLinkProps,
  PlaylistItem,
  ProductProps,
  MediaType,
} from '@repo/ui/src/types';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { type ProductPurchasing } from '@repo/ui/src/types/product';
import { type ErrorModalProps } from './components/ErrorModal';

// types
export enum ModalID {
  cart = 'cart',
  login = 'login',
  error = 'error',
  outOfStock = 'out-of-stock',
  country = 'country',
  image = 'image',
  grillRecommendation = 'grillRecommendation',
  video = 'video',
  languageSelector = 'language-selector',
  checkoutLoader = 'checkout-loader',
  learnMore = 'learn-more',
  gallery = 'gallery',
  threeSixtySpinViewer = '360-spin-viewer',
  productDetails = 'product-details',
}

export interface ProductDetailsData {
  title?: string;
  shortTitle?: string;
  mainImageUrl?: string;
  images?: Array<MediaType>;
  category?: string;
  variations?: Array<{ attributesMap?: { grill_size?: string }; grillSize?: string }>;
  variationValues?: Array<{ name: string; value: string }>;
  price?: number;
  link?: string;
  deliveryKey?: string;
  productUrl?: string;
  sku?: string;
  data?: {
    shopData: {
      upc?: string;
    };
  };
  description?: string;
  shortDescription?: string;
  ratingSummary?: {
    average?: number;
    count?: number;
  };
  isNewProduct?: boolean;
  discount?: number;
  isOrderable?: boolean;
  isComingSoon?: boolean;
  purchasing?: ProductPurchasing;
}

export type OpenModalsType = Map<ModalID, any | true>;
export type ListenerCB = (isOpen: boolean) => void;
export type ToggleListenerFunc = (id: ModalID, func: ListenerCB) => void;

export interface ModalData extends Record<ModalID, any> {
  [ModalID.cart]: void;
  [ModalID.login]: {
    redirect?: string;
    view?: AuthViews;
    isCheckout?: boolean;
    onSuccess?: () => void;
  } | void;
  [ModalID.outOfStock]: ProductProps;
  [ModalID.country]: void;
  [ModalID.error]: ErrorModalProps | void;
  [ModalID.learnMore]: ProductProps;
  [ModalID.image]: {
    instagramHandle?: string;
    image?: MediaType;
    imgLink?: Array<LinkProps>;
    recommendedProduct?: Array<any>;
  };
  [ModalID.grillRecommendation]: Exclude<ToutContentProps['grillRecommendation'], undefined>;
  [ModalID.languageSelector]: {
    regions?: Array<Region>;
    title?: string;
  };
  [ModalID.video]: {
    link?: { link: LinkProps };
    youtubeLink: YoutubeLinkProps;
    playlistItems?: Array<PlaylistItem>;
  };
  [ModalID.checkoutLoader]: void;
  [ModalID.gallery]: {
    images: Array<MediaType>;
    useImagePadding?: boolean;
  };
  [ModalID.threeSixtySpinViewer]: {
    url: string;
    spinSetCount: number;
    ariaLabel: string;
  };
  [ModalID.productDetails]: ProductDetailsData;
}
export type PushModal = <ID extends ModalID>(
  ...args: ModalData[ID] extends void ? [id: ID] : [id: ID, data: ModalData[ID]]
) => void;
export interface UIContextType {
  openModalsMap: OpenModalsType;
  openModalIds: Array<ModalID>;
  pushModal: PushModal;
  closeModal: (id?: ModalID) => void;
  enableID: (id: ModalID) => void;
  disableID: (id: ModalID) => void;
  addListener: ToggleListenerFunc;
  removeListener: ToggleListenerFunc;
}

export interface DrawerContextValue {
  inDrawer: boolean;
  close: () => void;
  closeAll: () => void;
  index: number;
  id: ModalID | null;
}

// component
export { UIProvider } from '@repo/ui/src/modules/UI/components/UIProvider';
export { DrawerHeader } from '@repo/ui/src/modules/UI/components/Drawer/_DrawerHeader';
export { default as Modal } from '@repo/ui/src/modules/UI/components/Modal';

// hooks
export { useDrawer } from '@repo/ui/src/modules/UI/hooks/useDrawer';
export { useModal } from '@repo/ui/src/modules/UI/hooks/useModal';
export { useUIListener } from '@repo/ui/src/modules/UI/hooks/useUIListener';
